import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../../lib/apiClient';
import { useToast } from '../../hooks/use-toast';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Code, Plus, Trash2, Loader2, Edit2, X } from 'lucide-react';

const SkillsManager = () => {
  const { toast } = useToast();
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editingSkill, setEditingSkill] = useState(null);
  const [formData, setFormData] = useState({
    category: '',
    icon: '',
    color: '',
    itemsText: '',
    order_num: 0,
  });

  const serializeItems = (items = []) =>
    items.map((item) => `${item.name}:${item.level}`).join('\n');

  const parseItems = (text) => {
    const lines = text
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);

    if (lines.length === 0) {
      throw new Error('Please add at least one skill item.');
    }

    return lines.map((line) => {
      const parts = line.split(/[:|]/);
      if (parts.length < 2) {
        throw new Error('Each item must be in the format "Name:Level".');
      }
      const name = parts[0].trim();
      const level = Number(parts[1].trim());

      if (!name) {
        throw new Error('Skill name is required for each item.');
      }
      if (Number.isNaN(level)) {
        throw new Error('Skill level must be a number (0-100).');
      }

      return { name, level };
    });
  };

  const loadSkills = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiClient.get('/api/admin/skills');
      setSkills(data || []);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load skills',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadSkills();
  }, [loadSkills]);

  const handleEdit = (skill) => {
    setEditingSkill(skill);
    setFormData({
      category: skill.category,
      icon: skill.icon,
      color: skill.color,
      itemsText: serializeItems(skill.items),
      order_num: skill.order_num,
    });
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditingSkill(null);
    setFormData({ category: '', icon: '', color: '', itemsText: '', order_num: 0 });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        category: formData.category,
        icon: formData.icon,
        color: formData.color,
        items: parseItems(formData.itemsText),
        order_num: Number.parseInt(formData.order_num, 10) || 0,
      };

      if (editingSkill) {
        await apiClient.put(`/api/admin/skills/${editingSkill.id}`, payload);
        toast({ title: 'Success', description: 'Skill updated successfully' });
      } else {
        await apiClient.post('/api/admin/skills', payload);
        toast({ title: 'Success', description: 'Skill created successfully' });
      }

      await loadSkills();
      handleCancel();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Operation failed',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this skill?')) return;
    try {
      await apiClient.delete(`/api/admin/skills/${id}`);
      toast({ title: 'Success', description: 'Skill deleted successfully' });
      await loadSkills();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete skill',
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="skills-manager">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Code className="w-8 h-8 text-cyan-500" />
          <h1 className="text-3xl font-bold text-white">Skills Management</h1>
        </div>
        {!isEditing && (
          <Button
            onClick={() => setIsEditing(true)}
            className="bg-cyan-500 hover:bg-cyan-600 text-black"
            data-testid="add-skill-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Skill
          </Button>
        )}
      </div>

      {isEditing && (
        <div className="bg-neutral-900 border border-cyan-500/50 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-white">
              {editingSkill ? 'Edit Skill' : 'Add New Skill'}
            </h2>
            <Button
              onClick={handleCancel}
              variant="ghost"
              size="sm"
              className="text-neutral-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-neutral-300">Category</label>
                <Input
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="bg-neutral-800 border-neutral-700 text-white"
                  required
                  placeholder="e.g., Frontend"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-neutral-300">Icon</label>
                <Input
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  className="bg-neutral-800 border-neutral-700 text-white"
                  required
                  placeholder="e.g., Monitor"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-neutral-300">Color</label>
                <Input
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="bg-neutral-800 border-neutral-700 text-white"
                  required
                  placeholder="e.g., cyan"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-neutral-300">Order Number</label>
                <Input
                  type="number"
                  value={formData.order_num}
                  onChange={(e) => setFormData({ ...formData, order_num: e.target.value })}
                  className="bg-neutral-800 border-neutral-700 text-white"
                />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-neutral-300">Items</label>
              <Textarea
                value={formData.itemsText}
                onChange={(e) => setFormData({ ...formData, itemsText: e.target.value })}
                className="bg-neutral-800 border-neutral-700 text-white font-mono text-sm"
                rows={6}
                required
                placeholder={'React:90\nTypeScript:85'}
              />
              <p className="text-xs text-neutral-500">One per line, format: Name:Level</p>
            </div>
            <Button type="submit" className="bg-cyan-500 hover:bg-cyan-600 text-black">
              {editingSkill ? 'Update' : 'Create'} Skill
            </Button>
          </form>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4">
        {skills.map((skill) => (
          <div
            key={skill.id}
            className="bg-neutral-900 border border-neutral-800 rounded-xl p-6 hover:border-neutral-700 transition-colors"
          >
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-white mb-2">{skill.category}</h3>
                <div className="text-sm text-neutral-400 space-y-1">
                  <p>Icon: {skill.icon} | Color: {skill.color} | Order: {skill.order_num}</p>
                  <p>Items: {skill.items?.length || 0} skills</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => handleEdit(skill)}
                  variant="outline"
                  size="sm"
                  className="border-neutral-700 text-neutral-300 hover:text-white"
                >
                  <Edit2 className="w-4 h-4" />
                </Button>
                <Button
                  onClick={() => handleDelete(skill.id)}
                  variant="outline"
                  size="sm"
                  className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {skills.length === 0 && !isEditing && (
        <div className="text-center py-12 text-neutral-500">
          No skills yet. Click "Add Skill" to get started.
        </div>
      )}
    </div>
  );
};

export default SkillsManager;
